import type { CmChecklist } from '@luxuryescapes/contract-svc-offer';

import type { ChecklistDetail } from '../../shared/checklistUtils';
import { formatAgeList } from '../../shared/format';
import type { EgsAgeCoverageRowView, EgsCapacityRowView, EgsMarginRowView } from '../../tabs/egs/types';
import type { WithChecklistItemMetadata } from '../../types';
import { presentRowBase } from '../rowBase';

export interface PresentedEgs {
  ageCoverage: Array<WithChecklistItemMetadata<EgsAgeCoverageRowView>>;
  capacity: Array<WithChecklistItemMetadata<EgsCapacityRowView>>;
  margin: Array<WithChecklistItemMetadata<EgsMarginRowView>>;
}

export function presentEgsRows({
  ageCoverageRows,
  capacityRows,
  marginRows,
}: {
  ageCoverageRows: Array<CmChecklist.EgsAgeCoverageRow>;
  capacityRows: Array<CmChecklist.EgsCapacityRow>;
  marginRows: Array<CmChecklist.EgsMarginRow>;
}): PresentedEgs {
  return {
    ageCoverage: ageCoverageRows.map(presentAgeCoverageRow),
    capacity: capacityRows.map((row, index) => ({
      ...presentRowBase(row),
      egsCapacity: row.evidence.egs_capacity ?? undefined,
      field: 'EGS capacity',
      includedCapacity: row.evidence.included_capacity ?? undefined,
      includedOccupancy: row.subject.included_occupancy ?? undefined,
      maxOccupancy: row.subject.max_occupancy ?? undefined,
      maxRoomCapacity: row.evidence.max_room_capacity ?? undefined,
      name: row.subject.package_name ?? `Package ${index + 1}`,
      roomName: row.subject.room_name || 'Room not mapped',
      roomRateId: row.subject.room_rate_id ?? undefined,
      roomTypeId: row.subject.room_type_id ?? undefined,
    })),
    margin: marginRows.map((row) => ({
      ...presentRowBase(row),
      ageRange: row.subject.age_range ?? undefined,
      beddingType: row.subject.bedding_type ?? undefined,
      collectedBy: row.subject.collected_by ?? undefined,
      costPrice: row.evidence.cost_price ?? undefined,
      currencyCode: row.subject.currency_code,
      extraGuestType: row.subject.extra_guest_type ?? undefined,
      field: 'Extra Guest Surcharges margin',
      inclusions: row.subject.inclusions ?? undefined,
      margin: row.evidence.margin ?? undefined,
      marginBand: row.evidence.margin_band ?? undefined,
      name: row.subject.name ?? row.subject.egs_id,
      opportunityName: row.subject.opportunity_id ?? undefined,
      sellPrice: row.evidence.sell_price ?? undefined,
    })),
  };
}

function presentAgeCoverageRow(row: CmChecklist.EgsAgeCoverageRow): WithChecklistItemMetadata<EgsAgeCoverageRowView> {
  const uncoveredAges = row.evidence.uncovered_ages.length > 0 ? formatAgeList(row.evidence.uncovered_ages) : undefined;

  return {
    ...presentRowBase(row),
    details: [
      ...presentAgeBands(row),
      { label: 'Uncovered ages', value: uncoveredAges ? capitalize(uncoveredAges) : 'None' },
    ],
    field: 'EGS age coverage',
    name: 'Extra guest age coverage',
    summary: uncoveredAges
      ? `No extra guest surcharge covers ${uncoveredAges}.`
      : 'Extra guest surcharge age ranges cover every age from 0 up.',
  };
}

// One data point per surcharge, grouped by guest type; ranges the server could not parse show as entered.
function presentAgeBands(row: CmChecklist.EgsAgeCoverageRow): Array<ChecklistDetail> {
  const bandsById = new Map(row.evidence.bands.map((band) => [band.egs_id, band]));

  return [...row.subject.surcharges]
    .sort(
      (a, b) =>
        (a.extra_guest_type ?? '').localeCompare(b.extra_guest_type ?? '') ||
        (bandsById.get(a.egs_id)?.from_age ?? Infinity) - (bandsById.get(b.egs_id)?.from_age ?? Infinity),
    )
    .map((surcharge) => {
      const band = bandsById.get(surcharge.egs_id);

      return {
        label: `${surcharge.extra_guest_type ?? 'Unknown type'} — ${surcharge.name ?? surcharge.egs_id}`,
        value: band
          ? capitalize(formatAgeList(rangeOf(band.from_age, band.to_age)))
          : `${surcharge.age_range ?? 'No age range'} (not recognised)`,
      };
    });
}

function rangeOf(from: number, to: number) {
  return Array.from({ length: to - from + 1 }, (_, index) => from + index);
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
